import React, { useCallback, useEffect, useState } from "react";
import { Box, Button, IconButton, Paper, Typography } from "@mui/material";
import { Icon } from "@iconify/react";

/**
 * "Install this app" prompt.
 *
 * Chrome/Edge/Android fire `beforeinstallprompt` when the app meets the install
 * criteria (manifest + a service worker with a fetch handler + HTTPS). The event
 * is the ONLY way to open the native install dialog, and it can only be used
 * once, so it is captured and held until the user acts on it.
 *
 * iOS/Safari never fires it — Add to Home Screen is manual there — so iOS gets a
 * short instruction instead of a button that could not work.
 */

const DISMISSED_KEY = "pwa-install-dismissed-at";
// How long a dismissal lasts before the prompt may reappear. Long enough not to
// nag, short enough that someone who said "not now" is asked again eventually.
const DISMISS_DAYS = 14;

const isStandalone = () =>
  window.matchMedia?.("(display-mode: standalone)")?.matches ||
  // iOS Safari reports installed state here rather than via display-mode.
  window.navigator.standalone === true;

const isIos = () =>
  /iphone|ipad|ipod/i.test(window.navigator.userAgent) &&
  !/crios|fxios/i.test(window.navigator.userAgent);

const wasRecentlyDismissed = () => {
  try {
    const at = Number(window.localStorage.getItem(DISMISSED_KEY));
    if (!at) return false;
    return Date.now() - at < DISMISS_DAYS * 24 * 60 * 60 * 1000;
  } catch {
    // Private mode / blocked storage: treat as never dismissed rather than
    // suppressing the prompt forever.
    return false;
  }
};

const PWAInstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [open, setOpen] = useState(false);
  const [iosHint, setIosHint] = useState(false);

  useEffect(() => {
    // Already installed, or recently dismissed: stay out of the way.
    if (isStandalone() || wasRecentlyDismissed()) return undefined;

    if (isIos()) {
      setIosHint(true);
      setOpen(true);
      return undefined;
    }

    const onBeforeInstallPrompt = (event) => {
      // Stop Chrome's own mini-infobar so this is the only prompt shown.
      event.preventDefault();
      setDeferredPrompt(event);
      setOpen(true);
    };

    const onInstalled = () => {
      setOpen(false);
      setDeferredPrompt(null);
    };

    window.addEventListener("beforeinstallprompt", onBeforeInstallPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstallPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const dismiss = useCallback(() => {
    setOpen(false);
    try {
      window.localStorage.setItem(DISMISSED_KEY, String(Date.now()));
    } catch {
      /* storage blocked — the prompt simply returns next load */
    }
  }, []);

  const install = useCallback(async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    try {
      await deferredPrompt.userChoice;
    } catch {
      /* the dialog was closed; nothing to do */
    }
    // The event is single-use: once prompt() has been called the browser will
    // not let it be reused, so drop it and close either way.
    setDeferredPrompt(null);
    setOpen(false);
  }, [deferredPrompt]);

  if (!open) return null;

  return (
    <Paper
      elevation={8}
      role="dialog"
      aria-label="Install Spandan One"
      sx={{
        position: "fixed",
        zIndex: 1400,
        left: { xs: 12, sm: "auto" },
        right: { xs: 12, sm: 24 },
        bottom: { xs: 12, sm: 24 },
        maxWidth: { sm: 380 },
        p: 2,
        borderRadius: "14px",
        display: "flex",
        alignItems: "flex-start",
        gap: 1.5,
      }}
    >
      <Box
        component="img"
        src="/spandan-one.svg"
        alt=""
        sx={{ width: 40, height: 40, flexShrink: 0, borderRadius: "10px" }}
      />

      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography sx={{ fontWeight: 700, fontSize: 15, fontFamily: "Figtree" }}>
          Install Spandan One
        </Typography>

        {iosHint ? (
          <Typography
            sx={{ fontSize: 13, color: "text.secondary", fontFamily: "Figtree", mt: 0.5 }}
          >
            Tap the Share button, then <strong>Add to Home Screen</strong>.
          </Typography>
        ) : (
          <>
            <Typography
              sx={{ fontSize: 13, color: "text.secondary", fontFamily: "Figtree", mt: 0.5 }}
            >
              Add it to your device for faster access and a full-screen view.
            </Typography>
            <Box sx={{ display: "flex", gap: 1, mt: 1.5 }}>
              <Button
                size="small"
                variant="contained"
                onClick={install}
                sx={{ textTransform: "none", borderRadius: "8px" }}
              >
                Install
              </Button>
              <Button
                size="small"
                onClick={dismiss}
                sx={{ textTransform: "none", borderRadius: "8px" }}
              >
                Not now
              </Button>
            </Box>
          </>
        )}
      </Box>

      <IconButton size="small" onClick={dismiss} aria-label="Dismiss">
        <Icon icon="mdi:close" width="18" />
      </IconButton>
    </Paper>
  );
};

export default PWAInstallPrompt;
